

import { useState, useEffect } from "react";
import axios from "axios";
import BasicInformation from "./BasicInformation";
import HealthGoals from "./Health";
import LifestylePreferences from "./LifeStyle";
import MedicalReports from "./Medical";
import { useNavigate, useLocation } from 'react-router-dom';

const LoadingModal = ({ isOpen, slogans = [], pdfStatus = "processing" }) => {
  const [currentSloganIndex, setCurrentSloganIndex] = useState(0);

  useEffect(() => {
    if (!isOpen || slogans.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSloganIndex((prev) => (prev + 1) % slogans.length);
    }, 7000);

    return () => clearInterval(interval);
  }, [isOpen, slogans.length]);

  if (!isOpen) return null;

  const currentSlogan = slogans[currentSloganIndex] || "";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] backdrop-blur-sm">
      <div className="rounded-2xl p-12 text-center max-w-md mx-4 relative overflow-hidden border border-gray-200 shadow-2xl bg-white">
        <div className="relative z-10">
          <div className="mb-8 flex justify-center">
            <div className="relative w-24 h-24">
              <div
                className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#00CFA5] border-r-[#00CFA5] animate-spin"
                style={{ animationDuration: "2s" }}
              ></div>
              <div
                className="absolute inset-2 rounded-full border-4 border-transparent border-b-[#004276] border-l-[#004276] animate-spin"
                style={{ animationDuration: "3s", animationDirection: "reverse" }}
              ></div>
              <div
                className="absolute inset-4 rounded-full flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, #00CFA5, #00B5E2)" }}
              >
                <span className="text-2xl">🥗</span>
              </div>
            </div>
          </div>

          <h3 className="text-sm font-medium text-gray-500 tracking-wide mb-6">
            {pdfStatus === "diet_creating"
              ? "💪 Smart Diet is crafting your perfect nutrition plan..."
              : "📄 Generating your personalized PDF report..."}
          </h3>

          <div className="mb-8 min-h-[90px] flex items-center justify-center">
            <p className="text-xl font-bold px-6 animate-slogan-fade relative leading-relaxed">
              {(() => {
                const parts = currentSlogan.split(
                  /(\p{Emoji_Presentation}|\p{Emoji}\uFE0F)/gu
                );

                return parts.map((part, idx) =>
                  /\p{Emoji_Presentation}|\p{Emoji}\uFE0F/u.test(part) ? (
                    <span key={idx}>{part}</span>
                  ) : (
                    <span
                      key={idx}
                      className="text-transparent bg-clip-text inline-block"
                      style={{
                        backgroundImage: "linear-gradient(90deg, #00CFA5, #00B5E2)",
                      }}
                    >
                      {part}
                    </span>
                  )
                );
              })()}
            </p>
          </div>

          <div className="flex justify-center gap-2 mb-2">
            <div
              className="w-6 h-1 rounded-full animate-pulse"
              style={{ background: "linear-gradient(90deg, #00CFA5, #00B5E2)" }}
            ></div>
            <div
              className="w-6 h-1 rounded-full animate-pulse"
              style={{ background: "linear-gradient(90deg, #00B5E2, #00CFA5)" }}
            ></div>
          </div>

          <p className="text-sm mt-2 font-medium"
            style={{ color: "var(--color-brand-primary)" }}
          >
            Don't close the page while loading.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes slogan-fade {
          0% { opacity: 0; transform: translateY(10px); }
          10% { opacity: 1; transform: translateY(0); }
          90% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-10px); }
        }
        .animate-slogan-fade {
          animation: slogan-fade 7s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default function DietRecommender() {
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pdfStatus, setPdfStatus] = useState("diet_creating");

  // ✅ Initialize directly from location.state to avoid timing issue
  const [patientInfo, setPatientInfo] = useState(location.state?.patientInfo || null);
  const [slogans, setSlogans] = useState(location.state?.slogans || []);

  const [formData, setFormData] = useState({
    patientid: "",
    name: "",
    age: "",
    gender: "",
    height: "",
    weight: "",
    mobile: "",
    country: "",
    state: "",
    region: "",
    language: "",
    goal: "",
    meal_plan: "",
    food_preference: "",
    diseases: "",
    exercise_type: "",
    sleep_pattern: "",
    activity_level: "",
    allergies: "",
    water_intake: "",
    stress_level: "",
    menstrual_health: "",
    additional_info: "",
    pdf_report: "",
    createdby: location.state?.createdby || "",   
    sourcechannel: location.state?.sourcechannel || "",
  });

  useEffect(() => {
    const patientCode = sessionStorage.getItem("patientCode");

    setFormData(prev => ({
      ...prev,
      patientid: patientCode
    }));

    // ✅ Already initialized from location.state, skip fetch
    if (location.state?.patientInfo) {
      console.log('✅ Using pre-loaded patient info from landing page');
      return;
    }

    // ✅ Fallback fetch if no pre-loaded data
    console.log('⚠️ No pre-loaded data, fetching patient info...');
    if (patientCode) {
      axios
        .get(
          `${process.env.REACT_APP_SMARTDIET_BASEURL}user/patientinfo?patient_id=${patientCode}`
        )
        .then((res) => {
          console.log("Patient Data (fallback fetch):", res.data);
          if (res.data) {
            const patientData = Array.isArray(res.data) ? res.data[0] : res.data.data?.[0];
            if (patientData) {
              setPatientInfo(patientData);
            }
            if (res.data.slogans && Array.isArray(res.data.slogans)) {
              setSlogans(res.data.slogans);
            }
          }
        })
        .catch((err) => {
          console.error("Error fetching patient info:", err);
        });
    }
  }, [location.state]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [step]);

  const handleNext = (data) => {
    console.log("Data received from step:", data);

    const updatedFormData = {
      patientid: data.patientid || formData.patientid,
      name: data.name || formData.name,
      age: data.age || formData.age,
      gender: data.gender || formData.gender,
      height: data.height || formData.height,
      weight: data.weight || formData.weight,
      mobile: data.mobile || formData.mobile,
      country: data.country || formData.country,
      state: data.state || formData.state,
      region: data.region || formData.region,
      language: data.language || formData.language,
      goal: data.goal || formData.goal,
      meal_plan: data.meal_plan || formData.meal_plan,
      food_preference: data.food_preference || formData.food_preference,
      diseases: data.diseases || formData.diseases,
      exercise_type: data.exercise_type || formData.exercise_type,
      sleep_pattern: data.sleep_pattern || formData.sleep_pattern,
      activity_level: data.activity_level || formData.activity_level,
      allergies: data.allergies || formData.allergies,
      water_intake: data.water_intake || formData.water_intake,
      stress_level: data.stress_level || formData.stress_level,
      menstrual_health: data.menstrual_health || formData.menstrual_health,
      additional_info: data.additional_info || formData.additional_info,
      pdf_report: data.pdf_report || formData.pdf_report,
      createdby: data.createdby || formData.createdby,        
    sourcechannel: data.sourcechannel || formData.sourcechannel, 
    };

    setFormData(updatedFormData);
    console.log("Updated formData:", updatedFormData);

    if (step === 4) {
      handleFinalSubmit(updatedFormData);
    } else {
      setStep(step + 1);
    }
  };

  const handlePrevious = (data = {}) => {
    console.log("Data received from previous:", data);

    const updatedFormData = {
      patientid: data.patientid || formData.patientid,
      name: data.name || formData.name,
      age: data.age || formData.age,
      gender: data.gender || formData.gender,
      height: data.height || formData.height,
      weight: data.weight || formData.weight,
      mobile: data.mobile || formData.mobile,
      country: data.country || formData.country,
      state: data.state || formData.state,
      region: data.region || formData.region,
      language: data.language || formData.language,
      goal: data.goal || formData.goal,
      meal_plan: data.meal_plan || formData.meal_plan,
      food_preference: data.food_preference || formData.food_preference,
      diseases: data.diseases || formData.diseases,
      exercise_type: data.exercise_type || formData.exercise_type,
      sleep_pattern: data.sleep_pattern || formData.sleep_pattern,
      activity_level: data.activity_level || formData.activity_level,
      allergies: data.allergies || formData.allergies,
      water_intake: data.water_intake || formData.water_intake,
      stress_level: data.stress_level || formData.stress_level,
      menstrual_health: data.menstrual_health || formData.menstrual_health,
      additional_info: data.additional_info || formData.additional_info,
      pdf_report: data.pdf_report || formData.pdf_report,
    };

    setFormData(updatedFormData);
    console.log("Updated formData from previous:", updatedFormData);
    setStep(step - 1);
  };

  const checkPdfComplete = async (orderid, language = "english") => {
    const isComplexLanguage = ["odia", "bengali", "tamil", "telugu", "malayalam"].includes(language?.toLowerCase());
    const maxRetries = isComplexLanguage ? 30 : 20;
    const retryDelay = isComplexLanguage ? 3000 : 2000;
    const maxWaitTime = isComplexLanguage ? 90000 : 40000;

    let retries = 0;
    const startTime = Date.now();

    console.log(`🔍 Starting PDF check for ${language} language`);

    while (retries < maxRetries) {
      try {
        const elapsedTime = Date.now() - startTime;

        if (elapsedTime > maxWaitTime) {
          console.warn(`⏰ Max wait time exceeded, proceeding to results...`);
          return {
            success: false,
            timeout: true,
            error: "PDF generation is taking longer than expected. Your report will be sent to you once ready.",
            orderid: orderid
          };
        }

        console.log(`🔍 Checking PDF status... Attempt ${retries + 1}/${maxRetries} (${Math.round(elapsedTime / 1000)}s elapsed)`);

        const response = await fetch(
          `${process.env.REACT_APP_SMARTDIET_BASEURL}check_pdf_complete?orderid=${orderid}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" }
          }
        );

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        console.log("📄 PDF Check Response:", data);

        if (data.status === "ready" && data.pdf_url) {
          console.log("✅ PDF is ready!");
          return {
            success: true,
            pdfUrl: data.pdf_url,
            whatsappStatus: data.whatsapp_status || null
          };
        }

        if (data.status === "processing" || data.status === "pending") {
          console.log(`⏳ PDF still ${data.status}, waiting...`);
          retries++;
          if (retries < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelay));
          }
        } else {
          console.error("❌ Unexpected PDF status:", data.status);
          return {
            success: false,
            error: `Unexpected status: ${data.status}`,
            response: data,
            orderid: orderid
          };
        }

      } catch (error) {
        console.error(`❌ Error checking PDF status (attempt ${retries + 1}):`, error);
        retries++;
        if (retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
    }

    console.warn("⚠️ Max retries reached, proceeding to results page...");
    return {
      success: false,
      timeout: true,
      error: "PDF generation is taking longer than expected. Your report will be sent to you once ready.",
      orderid: orderid
    };
  };

  const handleFinalSubmit = async (finalData) => {
    console.log("📤 Final Form Data to submit:", finalData);
    setLoading(true);
    setPdfStatus("diet_creating");

    try {
      console.log("🚀 Step 1: Submitting diet plan...");
      const response = await fetch(`${process.env.REACT_APP_SMARTDIET_BASEURL}smart-diet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(finalData),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const dietResponse = await response.json();
      console.log("✅ Diet Plan Created:", dietResponse);

      if (dietResponse.status !== "success") {
        throw new Error(dietResponse.message || "Failed to create diet plan");
      }

      const orderid = dietResponse.patient_info?.orderid;
      if (!orderid) {
        console.error("❌ No orderid in response:", dietResponse);
        throw new Error("No orderid received from diet plan creation");
      }

      console.log("📋 Order ID extracted:", orderid);
      console.log("🚀 Step 2: Checking PDF status...");
      setPdfStatus("pdf_generating");

      const pdfCheck = await checkPdfComplete(orderid, finalData.language);

      let enrichedResponse;

      if (pdfCheck.success) {
        enrichedResponse = {
          ...dietResponse,
          pdf_url: pdfCheck.pdfUrl,
          whatsapp_status: pdfCheck.whatsappStatus,
          pdf_ready: true
        };
        console.log("✅ PDF URL successfully added to response");
      } else if (pdfCheck.timeout) {
        enrichedResponse = {
          ...dietResponse,
          pdf_url: null,
          whatsapp_status: null,
          pdf_ready: false,
          pdf_pending: true,
          pdf_message: pdfCheck.error,
          orderid: pdfCheck.orderid
        };
        sessionStorage.setItem("pdfPendingOrderId", orderid);
        sessionStorage.setItem("pdfPendingLanguage", finalData.language || "english");
        console.warn("⚠️ PDF still generating, proceeding to results:", pdfCheck.error);
      } else {
        enrichedResponse = {
          ...dietResponse,
          pdf_url: null,
          whatsapp_status: null,
          pdf_ready: false,
          pdf_error: pdfCheck.error,
          orderid: pdfCheck.orderid
        };
        console.error("❌ PDF check failed:", pdfCheck.error);
      }

      console.log("📦 Final enriched response:", enrichedResponse);
      sessionStorage.setItem("dietResults", JSON.stringify(enrichedResponse));
      console.log("💾 Data stored in sessionStorage");

      setTimeout(() => {
        setLoading(false);
        console.log("🚀 Navigating to /result");
        navigate("/result");
      }, 1000);

    } catch (err) {
      console.error("❌ Error in handleFinalSubmit:", err);
      setLoading(false);
      alert(`Error processing diet plan: ${err.message}`);
    }
  };

  return (
    <div className="flex flex-col min-h-screen" style={{ backgroundColor: "#fff", backgroundImage: "var(--section-bg-gradient)" }}>
      <div className="pb-16 sm:pb-20"></div>

      <LoadingModal isOpen={loading} slogans={slogans} pdfStatus={pdfStatus} />

      <div className="flex flex-col px-4 sm:px-8 lg:px-16 xl:px-[max(64px,calc(50%_-_656px))] pt-2 sm:pt-3 pb-2" style={{ backgroundColor: "transparent" }}>
        <div className="flex flex-col gap-3 sm:gap-4 w-full max-w-[1290px] mx-auto">
          <div className="flex flex-col gap-1 w-full">
            <h1
  className="font-inter font-semibold text-xl sm:text-2xl lg:text-[32px] leading-tight lg:leading-[40px] w-fit"
  style={{
    backgroundImage: "var(--primary-gradient)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    color: "transparent",
  }}
>
  Welcome to Smart Diet Recommender
</h1>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center w-full mt-1 gap-2 sm:gap-0">
              <p className="font-inter text-sm sm:text-base leading-tight" style={{ color: "var(--color-brand-primary)" }}>
                Let's create your personalised Diet Plan
              </p>
              <p className="font-inter text-sm sm:text-[16px] leading-tight sm:leading-[20px]" style={{ color: "#3A3A3A" }}>
                Step {step} of 4
              </p>
            </div>
          </div>

          <div className="relative w-full h-[5px] sm:h-[6px] rounded-full overflow-hidden" style={{ backgroundColor: "#D1D1D6" }}>
            <div
  className="absolute top-0 left-0 h-full transition-all duration-700"
  style={{
    width: `${(step / 4) * 100}%`,
    backgroundImage: "var(--primary-gradient)"
  }}
></div>
          </div>
        </div>
      </div>

      <div className="flex-grow px-4 sm:px-8 lg:px-16 xl:px-[max(64px,calc(50%_-_656px))] py-2 sm:py-3">
        <div className="max-w-[1290px] mx-auto">
          {step === 1 && (
            <BasicInformation
              patientInfo={patientInfo}
              onNext={handleNext}
              patientData={formData}
            />
          )}

          {step === 2 && (
            <HealthGoals
              onNext={handleNext}
              onPrevious={handlePrevious}
              patientData={formData}
            />
          )}

          {step === 3 && (
            <LifestylePreferences
              onNext={handleNext}
              onPrevious={handlePrevious}
              patientData={formData}
            />
          )}

          {step === 4 && (
            <MedicalReports
              patientInfo={patientInfo}
              onNext={handleNext}
              onPrevious={handlePrevious}
              patientData={formData}
            />
          )}
        </div>
      </div>
    </div>
  );
}